import styles from "scss/Page.module.scss";
import AddButton from "components/Shared/Button/AddButton";
import { PointOfSale } from "../../components/PointOfSale";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { DynamicFilter } from "components/Shared/DynamicFilter";
import { useAppSelector } from "redux/store";
import { Box, Card, Grid } from "@mui/material";
import { useEffect, useMemo } from "react";
//import { useGetAllCategoriesQuery } from "redux/api/categoryApi";
import { FullScreenLoader, PageTitle } from "components";
import { EDynamicColumnType, IDynamicColumnFilter, ISelectOption } from "Interfaces/Utils";
import { IPointOfSaleState, removeCurrentFilters, updateFilters  } from "redux/features/pointOfSaleSlice";
import { useDeletePointOfSaleMutation, useGetPointOfSaleQuery } from "redux/api/pointOfSaleApi";
import { useGetAllCompaniesQuery } from "redux/api/companyApi";
import { useGetAllProvincesQuery } from "redux/api/provinceApi";
import { ICompany } from "Models/Company";
import { IUserLoggedState } from "redux/features/loggedUserSlice";
import { useIntl } from "react-intl";

/**
 * Component to render news main page
 */

export const PointOfSalePage = () => {
  const { formatMessage } = useIntl();

  const dispatch = useDispatch();
  const { user } = useAppSelector(
      (state: { userLoggedState: IUserLoggedState }) => state.userLoggedState
    );
  const PAGE_TITLE = formatMessage({ id: "pointOfSale.title" });
  const DELETE_SUCCESS_MESSAGE = formatMessage({ id: "pointOfSale.deleteSuccessMessage" });

  // App state selectors

  const { filters, pageIndex, pageSize, sort } = useAppSelector((state: { pointOfSaleState: IPointOfSaleState }) => state.pointOfSaleState);

  // API calls

  const { data: companies, isLoading: isCompaniesLoading } = useGetAllCompaniesQuery();
  const {data: provinces, isLoading: isProvincesLoading} = useGetAllProvincesQuery();
  const { data, isLoading } = useGetPointOfSaleQuery({
    filters: filters,
    pageIndex: pageIndex,
    pageSize: pageSize,
    sort: sort,
  });

  // Component state

  const [deletePointOfSale, { isSuccess: isPointOfSaleDeletedSuccess }] = useDeletePointOfSaleMutation();

  // If is correctly deleted, user is notified

  useEffect(() => {
     
    if (isPointOfSaleDeletedSuccess)
      toast.success(DELETE_SUCCESS_MESSAGE)

  }, [isPointOfSaleDeletedSuccess]);

  // Function to cast fetched groups to select options
  const provinceSelectOptions: ISelectOption[] = useMemo(() => {
      
      if (!provinces) return [];
      
       const pv =provinces.map((province) => ({
        label: province.name,
        value: province._id,
      }));
      return pv;
    }, [provinces])

  const groupSelectOptions: ISelectOption[] = useMemo(() => {

    if (!companies) return [];

    const cm= companies.map((company: ICompany) => ({
      label: company.name,
      value: company._id,
    }));
    return cm
    
    
  }, [companies])

  // Table column filters 
const columnFilters: IDynamicColumnFilter[] = useMemo(() => {
  const filters: IDynamicColumnFilter[] = [
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "name",
      label: formatMessage({ id: "pointOfSale.filterFields.name" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "addressType",
      label: formatMessage({ id: "pointOfSale.filterFields.addressType" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "addressName",
      label: formatMessage({ id: "pointOfSale.filterFields.addressName" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "addressNumber",
      label: formatMessage({ id: "pointOfSale.filterFields.addressNumber" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "addressDetails",
      label: formatMessage({ id: "pointOfSale.filterFields.addressDetails" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "postalCode",
      label: formatMessage({ id: "pointOfSale.filterFields.postalCode" }),
    },
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "locality",
      label: formatMessage({ id: "pointOfSale.filterFields.locality" }),
    },
    {
      type: EDynamicColumnType.SELECT,
      grid: 6,
      field: "province",
      label: formatMessage({ id: "pointOfSale.filterFields.province" }),
      values: provinceSelectOptions,
    },
  ];

  // Agregar el filtro de Empresa solo si el usuario tiene el rol de SUPERADMIN
  if (user?.role === "SUPERADMIN") {
    filters.push({
      type: EDynamicColumnType.SELECT,
      grid: 6,
      field: "companyId",
      label: formatMessage({ id: "pointOfSale.filterFields.companyId" }),
      values: groupSelectOptions,
    });
  }

  return filters;
}, [provinceSelectOptions, groupSelectOptions, user?.role]);


  // Table management

  const handleSubmit = (filters: Record<string, any>) => { 
    dispatch(updateFilters(filters));
  };

  const removeFilter = (code: string) => {
    dispatch(removeCurrentFilters(code));
  };

  const onDelete = (pointOfSaleId: string) => {
    deletePointOfSale(pointOfSaleId);
  };

  if (isLoading || isCompaniesLoading||isProvincesLoading) {
    return <FullScreenLoader />;
  }
  return (
    <Grid container className={styles.viewContainer}>
      <Grid item xs={12} >
        <Box display={"flex"} justifyContent={"space-between"}>
          <Box display={"flex"} alignItems={"center"} gap="20px" flexWrap={"wrap"}>
            <PageTitle pageTitle={PAGE_TITLE} />
            <DynamicFilter.AppliedFilters filters={columnFilters} filterValues={filters} handleRemoveFilter={removeFilter} />
          </Box>
          <Grid item xs={12} md={4}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  width: "480px !important",
                  position: "relative",
                }}
              >
                <DynamicFilter.Toolbar
                  filters={columnFilters}
                  externalFilterValues={filters}
                  filterData={handleSubmit}
                />

                <AddButton urlName={"/create-point-of-sale"} label={formatMessage({ id: "company.add" })} />
              </div>
            </div>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={16}>
        <Card className={styles.pageCard} sx={{ padding: "0" }}>
          <PointOfSale.Table
            deletePointOfSale={onDelete}
            paginatedQuery={data}
            isLoading={isLoading}
            companies={groupSelectOptions}
            provinces={provinceSelectOptions}
          />
        </Card>
      </Grid>
    </Grid>
  );
};

